/**
 * Dr. Jekyll & Mr. Hyde — „Unmatched: Cobble & Fog".
 *
 * THE SERUM: začína ako Dr. Jekyll, na začiatku ťahu sa smie premeniť.
 * Forma je verejná (`players[i].form`: `jekyll` / `hyde`). Ako Mr. Hyde
 * dostane po každom manévri 1 poškodenie.
 *
 * Karty majú banner „DR. JEKYLL", „MR. HYDE" alebo ANY — importér ich zlúčil
 * do jednej figúrky, preto rozdelenie nesie `BANNER` nižšie (zo zdroja,
 * pole `characterName`) a stráži ho `HeroSpecial.canPlay`.
 */

import { neighbors } from '../map.js';
import type { Ctx } from '../ops.js';
import {
  askChoice,
  cardDef,
  dealDamage,
  deckTopToBottom,
  discardFromHand,
  drawCard,
  drawSpecific,
  emit,
  getMap,
  getPlayer,
  healFighter,
} from '../ops.js';
import type { CardDefId, PlayerIndex } from '../types.js';
import { feint, skirmish } from './common.js';
import { askMove, emptySpacesInZone } from './helpers.js';
import type { CardEffect, EffectRegistry, HeroSpecial } from './types.js';

const HERO = 'jekyll-hyde';
const FOREVER = 'jekyll-hyde.forever-hyde';
const CALMING = 'jekyll-hyde.calming-research';
const PURE_EVIL = 'jekyll-hyde.pure-evil';
const SUCCUMB = 'jekyll-hyde.succumb-to-compulsion';
const RECOIL = 'jekyll-hyde.recoiling-blow';
const STRANGE = 'jekyll-hyde.strange-case';
/** Kľúčové slovo kariet s bannerom DR. JEKYLL (Forever Hyde ich odhadzuje). */
const JEKYLL_CARD = 'jekyll';

type Form = 'jekyll' | 'hyde';

/** Banner kariet podľa zdroja (`characterName`); ostatné sú ANY. */
const BANNER: Record<CardDefId, Form> = {
  'jekyll-hyde.distracted-triage': 'jekyll',
  [SUCCUMB]: 'jekyll',
  'jekyll-hyde.with-haste': 'jekyll',
  'jekyll-hyde.scientific-method': 'jekyll',
  [CALMING]: 'jekyll',
  'jekyll-hyde.madness-relents': 'hyde',
  [RECOIL]: 'hyde',
  [FOREVER]: 'hyde',
  [PURE_EVIL]: 'hyde',
  [STRANGE]: 'hyde',
};

export const formOf = (ctx: Ctx, player: PlayerIndex): Form =>
  ctx.state.players[player]?.form === 'hyde' ? 'hyde' : 'jekyll';

const heroOf = (ctx: Ctx, player: PlayerIndex) =>
  ctx.state.fighters.find((f) => f.owner === player && f.defId === HERO) ?? null;

function transform(ctx: Ctx, player: PlayerIndex, form: Form): void {
  const seat = ctx.state.players[player];
  if (!seat || seat.form === form) return;
  seat.form = form;
  emit(ctx, { type: 'specialUsed', player, heroId: HERO, name: form === 'hyde' ? 'Premena na Mr. Hyda' : 'Premena na Dr. Jekylla' });
}

/** Karta s bannerom DR. JEKYLL — kľúčové slovo pre výber pri Forever Hyde. */
const jekyllCard = (effect: CardEffect = {}): CardEffect => ({ ...effect, keywords: [JEKYLL_CARD] });

/**
 * „If you are Dr. Jekyll and playing this card to defend, this card's value
 * is 6 instead. If you are Mr. Hyde and playing this card to attack, this
 * card's value is 6 instead."
 */
const dualityOfMan: CardEffect = {
  during: (e, value) => {
    const form = formOf(e.ctx, e.player);
    if (form === 'jekyll' && e.role === 'defender') return 6;
    if (form === 'hyde' && e.role === 'attacker') return 6;
    return value;
  },
};

/** „If you won the combat, recover 2 health." */
const distractedTriage = jekyllCard({
  after: (e) => {
    const hero = heroOf(e.ctx, e.player);
    if (e.won && hero) healFighter(e.ctx, hero.id, 2);
  },
});

/** „Move up to 2 spaces. Transform to Mr. Hyde." — premena až po presune. */
const succumbToCompulsion = jekyllCard({
  after: (e) => {
    const hero = heroOf(e.ctx, e.player);
    if (hero) askMove(e, { fighters: [hero.id], max: 2, label: 'Succumb to Compulsion: presuň sa až o 2' });
    askChoice(e.ctx, {
      kind: 'auto',
      player: e.player,
      source: e.cardId,
      label: 'Succumb to Compulsion: premena na Mr. Hyda',
      optional: false,
      follow: { kind: 'effect', effect: SUCCUMB, data: 'hyde' },
    });
  },
  onFollow: (ctx, choice, form) => transform(ctx, choice.player, form as Form),
});

/** „Transform to Dr. Jekyll." */
const madnessRelents: CardEffect = {
  after: (e) => transform(e.ctx, e.player, 'jekyll'),
};

/** „Place Mr. Hyde in any space in his zone. Transform to Dr. Jekyll." */
const recoilingBlow: CardEffect = {
  after: (e) => {
    const hero = heroOf(e.ctx, e.player);
    const spaces = hero ? emptySpacesInZone(e.ctx, hero.id) : [];
    if (hero && spaces.length > 0) {
      askChoice(e.ctx, {
        kind: 'place',
        player: e.player,
        source: e.cardId,
        label: 'Recoiling Blow: polož Mr. Hyda v jeho zóne',
        optional: false,
        fighterId: hero.id,
        spaces,
      });
    }
    askChoice(e.ctx, {
      kind: 'auto',
      player: e.player,
      source: e.cardId,
      label: 'Recoiling Blow: premena na Dr. Jekylla',
      optional: false,
      follow: { kind: 'effect', effect: RECOIL, data: 'jekyll' },
    });
  },
  onFollow: (ctx, choice, form) => transform(ctx, choice.player, form as Form),
};

/** „You may discard Dr. Jekyll cards. Add 2 to this card's value for each card discarded." */
function askForever(ctx: Ctx, player: PlayerIndex, source: string, side: 'attacker' | 'defender'): void {
  askChoice(ctx, {
    kind: 'card',
    player,
    source,
    label: 'Forever Hyde: odhoď kartu Dr. Jekylla za +2 (alebo dosť)',
    optional: true,
    from: 'hand',
    purpose: 'effect',
    keyword: JEKYLL_CARD,
    discards: true,
    side,
  });
}

const foreverHyde: CardEffect = {
  duringChoice: (e) => askForever(e.ctx, e.player, e.cardId, e.role === 'defender' ? 'defender' : 'attacker'),
  onCardChosen: (ctx, choice, cardId) => {
    const combat = ctx.state.combat;
    if (!combat) return;
    discardFromHand(ctx, choice.player, cardId, 'effect');
    const side = choice.side ?? 'attacker';
    combat.boosts ??= { attacker: 0, defender: 0 };
    combat.boosts[side] += 2;
    askForever(ctx, choice.player, choice.source ?? FOREVER, side);
  },
};

/** „Move Dr. Jekyll up to 4 spaces." */
const withHaste = jekyllCard({
  after: (e) => {
    const hero = heroOf(e.ctx, e.player);
    if (hero) askMove(e, { fighters: [hero.id], max: 4, label: 'With Haste: presuň Dr. Jekylla až o 4' });
  },
});

/** „Draw a number of cards equal to the damage you were dealt." */
const scientificMethod = jekyllCard({
  after: (e) => {
    for (let i = 0; i < (e.damage ?? 0); i++) drawCard(e.ctx, e.player);
  },
});

/**
 * „Recover 2 health. Draw up to 3 cards. Keep one and put any others on the
 * bottom of your deck in any order." Tri vrchné karty vidí len on (voľba
 * `from: 'deck'`), nechá si jednu, poradie zvyšných pod balíčkom volí.
 */
const calmingResearch = jekyllCard({
  scheme: (e) => {
    const hero = heroOf(e.ctx, e.player);
    if (hero) healFighter(e.ctx, hero.id, 2);
    const top = getPlayer(e.ctx.state, e.player).deck.slice(0, 3);
    if (top.length === 0) return;
    askChoice(e.ctx, {
      kind: 'card',
      player: e.player,
      source: e.cardId,
      label: 'Calming Research: vyber kartu, ktorú si necháš',
      optional: false,
      from: 'deck',
      cards: top,
      purpose: 'effect',
      left: 3,
    });
  },
  onCardChosen: (ctx, choice, cardId) => {
    const rest = (choice.cards ?? []).filter((id) => id !== cardId);
    if (choice.left === 3) {
      drawSpecific(ctx, choice.player, cardId);
      if (rest.length === 2) {
        askChoice(ctx, {
          kind: 'card',
          player: choice.player,
          source: choice.source,
          label: 'Calming Research: ktorá z ostatných pôjde pod balíček prvá?',
          optional: true,
          from: 'deck',
          cards: rest,
          purpose: 'effect',
          left: 2,
          onSkip: { kind: 'effect', effect: CALMING, data: rest.join(',') },
        });
      } else {
        deckTopToBottom(ctx, choice.player, rest);
      }
      return;
    }
    deckTopToBottom(ctx, choice.player, [cardId, ...rest]);
  },
  // Preskočené poradie: pod balíček v pôvodnom poradí.
  onFollow: (ctx, choice, data) => deckTopToBottom(ctx, choice.player, data.split(',').filter(Boolean)),
});

/** „Place Mr. Hyde in any space in his zone. Mr. Hyde deals 2 damage to all adjacent fighters." */
const pureEvil: CardEffect = {
  scheme: (e) => {
    const hero = heroOf(e.ctx, e.player);
    const spaces = hero ? emptySpacesInZone(e.ctx, hero.id) : [];
    if (hero && spaces.length > 0) {
      askChoice(e.ctx, {
        kind: 'place',
        player: e.player,
        source: e.cardId,
        label: 'Pure Evil: polož Mr. Hyda v jeho zóne',
        optional: false,
        fighterId: hero.id,
        spaces,
      });
    }
    askChoice(e.ctx, {
      kind: 'auto',
      player: e.player,
      source: e.cardId,
      label: 'Pure Evil: 2 poškodenia susedom',
      optional: false,
      follow: { kind: 'effect', effect: PURE_EVIL, data: '' },
    });
  },
  onFollow: (ctx, choice) => {
    const hero = heroOf(ctx, choice.player);
    if (!hero) return;
    const around = new Set(neighbors(getMap(ctx), hero.spaceId));
    const targets = ctx.state.fighters.filter((f) => around.has(f.spaceId)).map((f) => f.id);
    for (const id of targets) dealDamage(ctx, id, 2);
  },
};

/**
 * „Reveal the top card of your deck. Deal damage equal to its BOOST value to
 * one adjacent fighter. Put the card in your hand."
 */
const strangeCase: CardEffect = {
  scheme: (e) => {
    const top = getPlayer(e.ctx.state, e.player).deck[0];
    const hero = heroOf(e.ctx, e.player);
    if (!top || !hero) return;
    emit(e.ctx, { type: 'cardRevealed', player: e.player, cardId: top });
    const amount = cardDef(e.ctx, top).boost;
    drawCard(e.ctx, e.player);
    const around = new Set(neighbors(getMap(e.ctx), hero.spaceId));
    const targets = e.ctx.state.fighters.filter((f) => around.has(f.spaceId)).map((f) => f.id);
    if (amount <= 0 || targets.length === 0) return;
    askChoice(e.ctx, {
      kind: 'damage',
      player: e.player,
      source: e.cardId,
      label: `Strange Case: ${amount} poškodenia susednej figúrke`,
      optional: false,
      amount,
      targets,
    });
  },
};

export const jekyllHydeCards: EffectRegistry = {
  'jekyll-hyde.feint': feint,
  'jekyll-hyde.skirmish': skirmish(2),
  'jekyll-hyde.duality-of-man': dualityOfMan,
  'jekyll-hyde.distracted-triage': distractedTriage,
  [SUCCUMB]: succumbToCompulsion,
  'jekyll-hyde.madness-relents': madnessRelents,
  [RECOIL]: recoilingBlow,
  [FOREVER]: foreverHyde,
  'jekyll-hyde.with-haste': withHaste,
  'jekyll-hyde.scientific-method': scientificMethod,
  [CALMING]: calmingResearch,
  [PURE_EVIL]: pureEvil,
  [STRANGE]: strangeCase,
};

export const jekyllHydeSpecial: HeroSpecial = {
  name: 'The Serum',
  /** „Start the game as Dr. Jekyll." */
  setup: (ctx, player) => {
    const seat = ctx.state.players[player];
    if (seat) seat.form = 'jekyll';
  },
  /** Jekyll hrá len karty „DR. JEKYLL" a ANY, Hyde len „MR. HYDE" a ANY. */
  canPlay: (ctx, player, def) => {
    const banner = BANNER[def.id];
    return !banner || banner === formOf(ctx, player);
  },
  /** „At the start of your turn, you may transform into Dr. Jekyll or Mr. Hyde." */
  startOfTurn: (ctx, player) => {
    if (!heroOf(ctx, player)) return;
    const other: Form = formOf(ctx, player) === 'jekyll' ? 'hyde' : 'jekyll';
    askChoice(ctx, {
      kind: 'confirm',
      player,
      source: null,
      label: other === 'hyde' ? 'The Serum: premeniť sa na Mr. Hyda?' : 'The Serum: premeniť sa na Dr. Jekylla?',
      optional: true,
      special: HERO,
      yes: other === 'hyde' ? 'Mr. Hyde' : 'Dr. Jekyll',
      no: 'Ostať',
    });
  },
  onConfirm: (ctx, player) => transform(ctx, player, formOf(ctx, player) === 'jekyll' ? 'hyde' : 'jekyll'),
  /** „While Mr. Hyde: After you Maneuver, take 1 damage." */
  afterManeuver: (ctx, player) => {
    const hero = heroOf(ctx, player);
    if (hero && formOf(ctx, player) === 'hyde') dealDamage(ctx, hero.id, 1);
  },
};
